import { activitiesApi } from './activitiesApi';
import { isUnauthorizedError } from './unwrap';

// One shared unread-count poller for every ActivityBell on the page. The nav
// renders a mobile bell and a desktop bell; both subscribe here so the page
// runs one interval, one visibilitychange listener and one request at a time.

export const UNREAD_POLL_MS = 60_000;

type Listener = () => void;

const listeners = new Set<Listener>();
let unread = 0;
let intervalId: number | null = null;
let inFlight: Promise<void> | null = null;
// Set when a background poll gets a 401. Polling stays off until a
// user-initiated request succeeds (resumeUnreadPolling) or the page reloads.
let stopped = false;

const emit = () => {
  listeners.forEach((listener) => listener());
};

const setUnread = (value: number) => {
  if (value === unread) return;
  unread = value;
  emit();
};

const isHidden = () => typeof document !== 'undefined' && document.hidden;

const onVisibilityChange = () => {
  if (!isHidden()) void refreshUnreadCount();
};

const startPolling = () => {
  if (typeof window === 'undefined' || stopped || intervalId !== null) return;
  intervalId = window.setInterval(() => {
    void refreshUnreadCount();
  }, UNREAD_POLL_MS);
  document.addEventListener('visibilitychange', onVisibilityChange);
};

const stopPolling = () => {
  if (intervalId !== null) {
    window.clearInterval(intervalId);
    intervalId = null;
  }
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityChange);
  }
};

export const refreshUnreadCount = (): Promise<void> => {
  if (stopped || isHidden()) return Promise.resolve();
  if (inFlight) return inFlight;

  inFlight = activitiesApi
    .unreadCount({ redirectOnUnauthorized: false })
    .then((data) => {
      setUnread(data.unread);
    })
    .catch((error: unknown) => {
      if (isUnauthorizedError(error)) {
        // Session expired: go quiet. No redirect, no error UI.
        stopped = true;
        stopPolling();
      }
      // Any other failure (404 = flag off, network blip) keeps the last count.
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

// Call after a user-initiated request succeeds, which proves the session is
// valid again. Restarts polling if a background 401 had stopped it.
export const resumeUnreadPolling = () => {
  if (!stopped) return;
  stopped = false;
  if (listeners.size > 0) {
    startPolling();
    void refreshUnreadCount();
  }
};

export const subscribeUnreadCount = (listener: Listener): (() => void) => {
  listeners.add(listener);
  if (listeners.size === 1) {
    startPolling();
    void refreshUnreadCount();
  }

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      stopPolling();
      // Nothing is mounted any more; the next mount behaves like a page load.
      stopped = false;
    }
  };
};

export const getUnreadCount = () => unread;

// Lets a page that already knows the count (for example the dashboard after
// mark all read) update every bell without waiting for the next poll.
export const publishUnreadCount = (value: number) => {
  setUnread(value);
};

export const isUnreadPollingStopped = () => stopped;

// Test helper: return the module to its page-load state.
export const resetUnreadCountStore = () => {
  stopPolling();
  listeners.clear();
  unread = 0;
  inFlight = null;
  stopped = false;
};
